'use client'

import { FC, useEffect, useState } from 'react'
import Image from 'next/image'
import { useGetTranslations, useConfig } from '@/root/shared-hooks'
import { Banner, Button, Checkbox } from '@/root/ui/tuuleliinid'
import { IdKaartIcon } from '@/root/ui/tuuleliinid/icons/IdKaartIcon'
import { getAuthErrorMessage } from '@/root/modules/tuuleliinid/LoginPage/helpers'
import { useIdKaartAuth } from '../../hooks/useIdKaartAuth'
import type { AuthVariant } from '../../constants'

interface IdKaartAuthProps {
  variant: AuthVariant
  onLoadingChange?: (isLoading: boolean) => void
  onSuccess?: (accessToken: string) => void | Promise<void>
}

export const IdKaartAuth: FC<IdKaartAuthProps> = ({ variant, onLoadingChange, onSuccess }) => {
  const { getTranslation } = useGetTranslations()
  const { theme } = useConfig()
  const { logInWithIdKaart, isLoading, error } = useIdKaartAuth({ onSuccess })
  const [rememberMe, setRememberMe] = useState(false)

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const isSignup = variant === 'signup'

  const buttonText = isLoading
    ? getTranslation('authIdKaartLoading')
    : isSignup
      ? getTranslation('authIdKaartSignup')
      : getTranslation('authIdKaartLogin')

  return (
    <div className="flex w-full flex-col items-center">
      <IdKaartIcon width={24} height={24} />

      <p className="text-primary-base mt-2 text-lg font-bold">{getTranslation('authIdKaartTitle')}</p>

      <p className="text-base-normal mt-2 text-center text-sm">{getTranslation('authIdKaartDescription')}</p>

      <Image
        src={`/${theme}/icons/IdKaartIllustration.svg`}
        alt="ID-kaart illustration"
        width={120}
        height={78}
        className="my-6"
      />

      {error && (
        <Banner
          variant="error"
          align="left"
          body={getAuthErrorMessage(error, getTranslation)}
          className="mb-4 w-full"
        />
      )}

      {!isSignup && (
        <Checkbox
          checked={rememberMe}
          onChange={() => setRememberMe((prev) => !prev)}
          variant="success"
          className="mb-6 self-start text-sm"
          label={getTranslation('authIdKaartRememberMe')}
        />
      )}

      <Button
        variant="contained"
        disabled={isLoading}
        onClick={() => logInWithIdKaart(rememberMe)}
        className="flex h-12 w-full items-center justify-center rounded-2xl text-lg font-bold whitespace-nowrap disabled:hover:brightness-100"
      >
        {buttonText}
      </Button>
    </div>
  )
}
